import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Store, Palette, Phone, CreditCard, Settings as SettingsIcon, 
  Save, Upload, Globe, ImageIcon, Shield, Key, Percent, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerSettings = () => {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    contact_phone: "",
    contact_email: "",
    logo_url: "",
    brand_color: "#0EA5E9",
    enable_table_number: true,
    enable_online_payments: false,
  });
  
  const [paymentSettings, setPaymentSettings] = useState({
    // EVC Plus
    evc_plus_enabled: false,
    evc_plus_merchant_id: "",
    evc_plus_api_user: "",
    evc_plus_api_key: "",
    evc_plus_api_secret: "",
    // Zaad
    zaad_enabled: false,
    zaad_merchant_phone: "",
    zaad_api_key: "",
    zaad_api_secret: "",
    // E-Dahab
    edahab_enabled: false,
    edahab_merchant_phone: "",
    edahab_api_key: "",
    edahab_secret_key: "",
    edahab_agent_code: "",
    edahab_preferred_method: "web",
    // Cash and Pay Later
    cash_enabled: true,
    pay_later_enabled: true,
    // VAT
    vat_enabled: false,
    vat_percentage: 0,
    // Manual Providers
    manual_providers: [],
  });

  const [newProvider, setNewProvider] = useState({
    name: "",
    merchant_number: "",
    ussd_code: "",
    instructions: "",
    notes: "",
    active: true,
    provider_type: "custom"
  });

  // Quick-fill presets for Somalia mobile money providers
  const PROVIDER_PRESETS = {
    evc_plus: { name: "Hormuud EVC Plus", ussd_code: "*712*{merchant}*{total}#", provider_type: "evc_plus" },
    zaad: { name: "Zaad", ussd_code: "*882*{merchant}*{total}#", provider_type: "zaad" },
    edahab: { name: "E-Dahab", ussd_code: "*770*{merchant}*{total}#", provider_type: "edahab" },
    sahal: { name: "Sahal (Golis)", ussd_code: "*883*{merchant}*{total}#", provider_type: "sahal" },
    custom: { name: "", ussd_code: "", provider_type: "custom" },
  };

  const applyPreset = (key) => {
    const p = PROVIDER_PRESETS[key];
    if (!p) return;
    setNewProvider(prev => ({
      ...prev,
      name: p.name,
      ussd_code: p.ussd_code,
      provider_type: p.provider_type,
    }));
  };

  useEffect(() => {
    fetchRestaurant();
  }, []);

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(`${API}/restaurant/profile`);
      setRestaurant(response.data);
      setFormData({
        name: response.data.name || "",
        address: response.data.address || "",
        city: response.data.city || "",
        contact_phone: response.data.contact_phone || "",
        contact_email: response.data.contact_email || "",
        logo_url: response.data.logo_url || "",
        brand_color: response.data.brand_color || "#0EA5E9",
        enable_table_number: response.data.enable_table_number ?? true,
        enable_online_payments: response.data.enable_online_payments ?? false,
      });
      setPaymentSettings({
        evc_plus_enabled: response.data.evc_plus_enabled ?? false,
        evc_plus_merchant_id: response.data.evc_plus_merchant_id || "",
        evc_plus_api_user: response.data.evc_plus_api_user || "",
        evc_plus_api_key: response.data.evc_plus_api_key || "",
        evc_plus_api_secret: response.data.evc_plus_api_secret || "",
        zaad_enabled: response.data.zaad_enabled ?? false,
        zaad_merchant_phone: response.data.zaad_merchant_phone || "",
        zaad_api_key: response.data.zaad_api_key || "",
        zaad_api_secret: response.data.zaad_api_secret || "",
        edahab_enabled: response.data.edahab_enabled ?? false,
        edahab_merchant_phone: response.data.edahab_merchant_phone || "",
        edahab_api_key: response.data.edahab_api_key || "",
        edahab_secret_key: response.data.edahab_secret_key || "",
        edahab_agent_code: response.data.edahab_agent_code || "",
        edahab_preferred_method: response.data.edahab_preferred_method || "web",
        cash_enabled: response.data.cash_enabled ?? true,
        pay_later_enabled: response.data.pay_later_enabled ?? true,
        vat_enabled: response.data.vat_enabled ?? false,
        vat_percentage: response.data.vat_percentage ?? 0,
        manual_providers: response.data.manual_providers || [],
      });
    } catch (error) {
      console.error("Failed to fetch restaurant:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save BOTH profile and payment settings together so the top-level
      // "Save Changes" button always persists everything visible on the page.
      const [profileResp, paymentResp] = await Promise.all([
        axios.put(`${API}/restaurant/profile`, formData),
        axios.put(`${API}/restaurant/payment-settings`, paymentSettings),
      ]);
      setRestaurant(paymentResp.data);
      // Sync paymentSettings from saved response so any backend-computed
      // fields (e.g. enable_online_payments) reflect the new state.
      setPaymentSettings(prev => ({
        ...prev,
        manual_providers: paymentResp.data.manual_providers || prev.manual_providers,
        evc_plus_enabled: paymentResp.data.evc_plus_enabled ?? prev.evc_plus_enabled,
        zaad_enabled: paymentResp.data.zaad_enabled ?? prev.zaad_enabled,
        edahab_enabled: paymentResp.data.edahab_enabled ?? prev.edahab_enabled,
        cash_enabled: paymentResp.data.cash_enabled ?? prev.cash_enabled,
        pay_later_enabled: paymentResp.data.pay_later_enabled ?? prev.pay_later_enabled,
        vat_enabled: paymentResp.data.vat_enabled ?? prev.vat_enabled,
        vat_percentage: paymentResp.data.vat_percentage ?? prev.vat_percentage,
      }));
      setFormData(prev => ({
        ...prev,
        enable_online_payments: paymentResp.data.enable_online_payments
      }));
      toast.success("All settings saved!");
    } catch (error) {
      console.error("Save failed:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Silent helper: persist the latest paymentSettings without showing a toast.
  // Used by add/edit/toggle/remove provider actions so changes survive refresh.
  const persistPaymentSettings = async (nextSettings) => {
    try {
      const r = await axios.put(`${API}/restaurant/payment-settings`, nextSettings);
      setRestaurant(r.data);
    } catch (e) {
      console.error("Auto-save payment settings failed:", e);
      toast.error("Couldn't save provider — please try Save Changes");
    }
  };

  const handleSavePaymentSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/restaurant/payment-settings`, paymentSettings);
      setRestaurant(response.data);
      // Sync paymentSettings from saved response to keep UI in sync
      setPaymentSettings(prev => ({
        ...prev,
        manual_providers: response.data.manual_providers || prev.manual_providers,
        evc_plus_enabled: response.data.evc_plus_enabled ?? prev.evc_plus_enabled,
        zaad_enabled: response.data.zaad_enabled ?? prev.zaad_enabled,
        edahab_enabled: response.data.edahab_enabled ?? prev.edahab_enabled,
        cash_enabled: response.data.cash_enabled ?? prev.cash_enabled,
        pay_later_enabled: response.data.pay_later_enabled ?? prev.pay_later_enabled,
        vat_enabled: response.data.vat_enabled ?? prev.vat_enabled,
        vat_percentage: response.data.vat_percentage ?? prev.vat_percentage,
      }));
      setFormData(prev => ({
        ...prev,
        enable_online_payments: response.data.enable_online_payments
      }));
      toast.success("Payment settings saved!");
    } catch (error) {
      toast.error("Failed to save payment settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large. Max 2MB allowed.");
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const response = await axios.post(`${API}/restaurant/logo`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setRestaurant(prev => ({ ...prev, logo_data: response.data.logo_data }));
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const addManualProvider = () => {
    if (!newProvider.name || !newProvider.merchant_number) {
      toast.error("Provider name and merchant number are required");
      return;
    }
    const next = {
      ...paymentSettings,
      manual_providers: [...paymentSettings.manual_providers, { ...newProvider, id: Date.now().toString() }]
    };
    setPaymentSettings(next);
    setNewProvider({ name: "", merchant_number: "", ussd_code: "", instructions: "", notes: "", active: true, provider_type: "custom" });
    persistPaymentSettings(next);
    toast.success("Provider saved");
  };

  const removeManualProvider = (id) => {
    const next = {
      ...paymentSettings,
      manual_providers: paymentSettings.manual_providers.filter(p => p.id !== id)
    };
    setPaymentSettings(next);
    persistPaymentSettings(next);
  };

  const toggleProviderActive = (id) => {
    const next = {
      ...paymentSettings,
      manual_providers: paymentSettings.manual_providers.map(p =>
        p.id === id ? { ...p, active: !(p.active ?? true) } : p
      )
    };
    setPaymentSettings(next);
    persistPaymentSettings(next);
  };

  // Debounced inline-edit persistence so each keystroke doesn't hit the API.
  const editTimer = useRef(null);
  const updateProviderField = (id, field, value) => {
    setPaymentSettings(prev => {
      const next = {
        ...prev,
        manual_providers: prev.manual_providers.map(p =>
          p.id === id ? { ...p, [field]: value } : p
        )
      };
      if (editTimer.current) clearTimeout(editTimer.current);
      editTimer.current = setTimeout(() => persistPaymentSettings(next), 600);
      return next;
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const isPro = restaurant?.subscription_plan === "professional" || restaurant?.subscription_plan === "pro" || restaurant?.subscription_plan === "enterprise";

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl" data-testid="manager-settings">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Restaurant Settings</h1>
              <p className="text-sm text-muted-foreground">Manage your restaurant profile & payments</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="tax">VAT/Tax</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Restaurant Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="restaurant-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Mogadishu"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="252612345678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Order Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Enable Table Numbers</p>
                    <p className="text-sm text-muted-foreground">
                      Ask customers for table number when ordering eat-in
                    </p>
                  </div>
                  <Switch
                    checked={formData.enable_table_number}
                    onCheckedChange={(checked) => setFormData({ ...formData, enable_table_number: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding" className="mt-6 space-y-6">
            {!isPro && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Professional Plan Required</p>
                      <p className="text-sm text-amber-700">
                        Custom branding (logo & colors) is available on Professional plan and above. 
                        You can upload a logo but it won't be displayed to customers.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Restaurant Logo
                </CardTitle>
                <CardDescription>
                  Upload your restaurant logo. PNG or JPG, max 2MB.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl bg-muted flex items-center justify-center overflow-hidden border-2 border-dashed">
                    {restaurant?.logo_data ? (
                      <img src={restaurant.logo_data} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingLogo ? "Uploading..." : "Upload Logo"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Recommended: 200x200px square image
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Brand Color
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Primary Brand Color</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.brand_color}
                      onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                      className="w-12 h-12 rounded-lg border border-border cursor-pointer"
                    />
                    <Input
                      value={formData.brand_color}
                      onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                      className="w-32 font-mono"
                    />
                    <div 
                      className="h-12 flex-1 rounded-lg flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: formData.brand_color }}
                    >
                      Preview Button
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Live Mobile Money Integration
                </CardTitle>
                <CardDescription>
                  Configure your payment credentials. Payments go directly to your accounts.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* EVC Plus (WaafiPay) */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">E</span>
                      </div>
                      <div>
                        <p className="font-medium">EVC Plus (WaafiPay)</p>
                        <p className="text-sm text-muted-foreground">Hormuud mobile payments</p>
                      </div>
                    </div>
                    <Switch
                      checked={paymentSettings.evc_plus_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, evc_plus_enabled: checked })}
                      data-testid="evc-plus-switch"
                    />
                  </div>
                  {paymentSettings.evc_plus_enabled && (
                    <div className="space-y-3 pt-2 border-t">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Merchant ID</Label>
                          <Input
                            value={paymentSettings.evc_plus_merchant_id}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, evc_plus_merchant_id: e.target.value })}
                            placeholder="Your WaafiPay Merchant ID"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>API User</Label>
                          <Input
                            value={paymentSettings.evc_plus_api_user}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, evc_plus_api_user: e.target.value })}
                            placeholder="API Username"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            value={paymentSettings.evc_plus_api_key}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, evc_plus_api_key: e.target.value })}
                            placeholder="API Key"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>API Secret</Label>
                          <Input
                            type="password"
                            value={paymentSettings.evc_plus_api_secret}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, evc_plus_api_secret: e.target.value })}
                            placeholder="API Secret"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Zaad */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">Z</span>
                      </div>
                      <div>
                        <p className="font-medium">Zaad (Telesom)</p>
                        <p className="text-sm text-muted-foreground">Telesom mobile payments</p>
                      </div>
                    </div>
                    <Switch
                      checked={paymentSettings.zaad_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, zaad_enabled: checked })}
                      data-testid="zaad-switch"
                    />
                  </div>
                  {paymentSettings.zaad_enabled && (
                    <div className="space-y-3 pt-2 border-t">
                      <div className="space-y-2">
                        <Label>Merchant Phone Number</Label>
                        <Input
                          value={paymentSettings.zaad_merchant_phone}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, zaad_merchant_phone: e.target.value })}
                          placeholder="252634xxxxxx"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            value={paymentSettings.zaad_api_key}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, zaad_api_key: e.target.value })}
                            placeholder="API Key"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>API Secret</Label>
                          <Input
                            type="password"
                            value={paymentSettings.zaad_api_secret}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, zaad_api_secret: e.target.value })}
                            placeholder="API Secret"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* E-Dahab */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">D</span>
                      </div>
                      <div>
                        <p className="font-medium">E-Dahab (Somtel)</p>
                        <p className="text-sm text-muted-foreground">Somtel mobile payments</p>
                      </div>
                    </div>
                    <Switch
                      checked={paymentSettings.edahab_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, edahab_enabled: checked })}
                      data-testid="edahab-switch"
                    />
                  </div>
                  {paymentSettings.edahab_enabled && (
                    <div className="space-y-3 pt-2 border-t">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Merchant Phone Number</Label>
                          <Input
                            value={paymentSettings.edahab_merchant_phone}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, edahab_merchant_phone: e.target.value })}
                            placeholder="252907xxxxxx"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Agent Code</Label>
                          <Input
                            value={paymentSettings.edahab_agent_code}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, edahab_agent_code: e.target.value })}
                            placeholder="Agent Code"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            value={paymentSettings.edahab_api_key}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, edahab_api_key: e.target.value })}
                            placeholder="API Key"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Secret Key</Label>
                          <Input
                            type="password"
                            value={paymentSettings.edahab_secret_key}
                            onChange={(e) => setPaymentSettings({ ...paymentSettings, edahab_secret_key: e.target.value })}
                            placeholder="Secret Key"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Payment Method</Label>
                        <Select 
                          value={paymentSettings.edahab_preferred_method}
                          onValueChange={(v) => setPaymentSettings({ ...paymentSettings, edahab_preferred_method: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="web">Web Payment (Invoice Link)</SelectItem>
                            <SelectItem value="popup">Pop-Up Payment (65/66 numbers)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Pop-up only works for numbers starting with 65 or 66. Others will fallback to web.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cash Payment */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">$</span>
                      </div>
                      <div>
                        <p className="font-medium">Cash Payment</p>
                        <p className="text-sm text-muted-foreground">Accept cash payments</p>
                      </div>
                    </div>
                    <Switch
                      checked={paymentSettings.cash_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, cash_enabled: checked })}
                    />
                  </div>
                </div>

                {/* Pay Later / Receipt */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                        <span className="text-white text-xs font-bold">📋</span>
                      </div>
                      <div>
                        <p className="font-medium">Pay Later / Receipt</p>
                        <p className="text-sm text-muted-foreground">Allow customers to pay at cashier</p>
                      </div>
                    </div>
                    <Switch
                      checked={paymentSettings.pay_later_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, pay_later_enabled: checked })}
                    />
                  </div>
                </div>

                {/* Manual Payment Providers */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div>
                    <p className="font-medium">Payment Accounts (Shown on Receipts & Checkout)</p>
                    <p className="text-sm text-muted-foreground">These accounts appear on printed receipts and customer checkout. Use templates like <code className="text-xs bg-gray-100 px-1 rounded">*712*{`{merchant}`}*{`{total}`}#</code> — the system fills the order total automatically.</p>
                  </div>

                  {/* Existing providers — editable */}
                  {paymentSettings.manual_providers.length > 0 && (
                    <div className="space-y-2">
                      {paymentSettings.manual_providers.map((provider) => (
                        <div key={provider.id} className={`border rounded-lg p-3 space-y-2 ${provider.active === false ? "bg-gray-100 opacity-70" : "bg-white"}`} data-testid={`provider-${provider.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <Input
                              value={provider.name}
                              onChange={(e) => updateProviderField(provider.id, "name", e.target.value)}
                              placeholder="Provider Name"
                              className="font-semibold flex-1"
                            />
                            <div className="flex items-center gap-2 shrink-0">
                              <Switch
                                checked={provider.active ?? true}
                                onCheckedChange={() => toggleProviderActive(provider.id)}
                                data-testid={`provider-active-${provider.id}`}
                              />
                              <span className={`text-xs font-medium ${provider.active === false ? "text-gray-500" : "text-emerald-600"}`}>
                                {provider.active === false ? "OFF" : "ON"}
                              </span>
                              <Button variant="ghost" size="sm" onClick={() => removeManualProvider(provider.id)} data-testid={`remove-provider-${provider.id}`}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={provider.merchant_number}
                              onChange={(e) => updateProviderField(provider.id, "merchant_number", e.target.value)}
                              placeholder="Merchant Number"
                              className="font-mono"
                            />
                            <Input
                              value={provider.ussd_code || ""}
                              onChange={(e) => updateProviderField(provider.id, "ussd_code", e.target.value)}
                              placeholder="USSD Template e.g., *712*{merchant}*{total}#"
                              className="font-mono text-xs"
                            />
                          </div>
                          <Input
                            value={provider.instructions || ""}
                            onChange={(e) => updateProviderField(provider.id, "instructions", e.target.value)}
                            placeholder="Customer instructions (optional)"
                            className="text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Quick Add via preset */}
                  <div className="border-t pt-3 space-y-2">
                    <p className="text-sm font-semibold">Quick Add Provider</p>
                    <div className="grid grid-cols-5 gap-1">
                      {[
                        { key: "evc_plus", label: "EVC Plus", color: "bg-orange-100 hover:bg-orange-200 text-orange-800 border-orange-300" },
                        { key: "zaad", label: "Zaad", color: "bg-green-100 hover:bg-green-200 text-green-800 border-green-300" },
                        { key: "edahab", label: "E-Dahab", color: "bg-blue-100 hover:bg-blue-200 text-blue-800 border-blue-300" },
                        { key: "sahal", label: "Sahal", color: "bg-purple-100 hover:bg-purple-200 text-purple-800 border-purple-300" },
                        { key: "custom", label: "Custom", color: "bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-300" },
                      ].map(p => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => applyPreset(p.key)}
                          className={`text-xs font-semibold px-2 py-1.5 rounded border ${p.color} transition-colors`}
                          data-testid={`preset-${p.key}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Provider Name (e.g., Hormuud EVC)"
                        value={newProvider.name}
                        onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                        data-testid="new-provider-name"
                      />
                      <Input
                        placeholder="Merchant Number (e.g., 612345678)"
                        value={newProvider.merchant_number}
                        onChange={(e) => setNewProvider({ ...newProvider, merchant_number: e.target.value })}
                        className="font-mono"
                        data-testid="new-provider-merchant"
                      />
                    </div>
                    <Input
                      placeholder="USSD Template (e.g., *712*{merchant}*{total}#)"
                      value={newProvider.ussd_code}
                      onChange={(e) => setNewProvider({ ...newProvider, ussd_code: e.target.value })}
                      className="font-mono text-xs"
                      data-testid="new-provider-ussd"
                    />
                    <Input
                      placeholder="Customer instructions (optional)"
                      value={newProvider.instructions}
                      onChange={(e) => setNewProvider({ ...newProvider, instructions: e.target.value })}
                      className="text-xs"
                    />
                    <Button variant="outline" onClick={addManualProvider} data-testid="add-provider-btn" className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Provider
                    </Button>
                  </div>

                  {paymentSettings.manual_providers.length > 0 && (
                    <div className="p-3 bg-gray-50 rounded-lg border border-dashed">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Receipt Preview (only ACTIVE providers shown):</p>
                      <div className="font-mono text-xs space-y-0.5">
                        {paymentSettings.manual_providers.filter(p => p.active !== false).map((p, i) => (
                          <div key={i} className="flex justify-between">
                            <span>{p.name}</span>
                            <span className="font-bold">{p.merchant_number}</span>
                          </div>
                        ))}
                        {paymentSettings.manual_providers.filter(p => p.active !== false).length === 0 && (
                          <p className="text-gray-400 italic">No active providers — receipts won't show payment accounts.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <Button onClick={handleSavePaymentSettings} disabled={saving} className="w-full">
                  <Save className="w-4 h-4 mr-2" />
                  Save Payment Settings
                </Button>

                <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sky-800">Secure & Direct Payments</p>
                      <p className="text-sm text-sky-700">
                        Dalabsom never handles your money. All payments are sent directly 
                        from customers to your mobile money account. We only initiate and track payments.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tax" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  VAT / Tax Settings
                </CardTitle>
                <CardDescription>
                  Configure tax settings for your orders
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Enable VAT</p>
                    <p className="text-sm text-muted-foreground">Add VAT to all orders</p>
                  </div>
                  <Switch
                    checked={paymentSettings.vat_enabled}
                    onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, vat_enabled: checked })}
                  />
                </div>

                {paymentSettings.vat_enabled && (
                  <div className="space-y-2">
                    <Label>VAT Percentage (%)</Label>
                    <Input
                      type="number"
                      value={paymentSettings.vat_percentage}
                      onChange={(e) => setPaymentSettings({ ...paymentSettings, vat_percentage: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 5"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-muted-foreground">
                      VAT will be calculated and added to every order. It will appear on receipts and checkout.
                    </p>
                  </div>
                )}

                <Button onClick={handleSavePaymentSettings} disabled={saving} className="w-full">
                  <Save className="w-4 h-4 mr-2" />
                  Save Tax Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default ManagerSettings;
